import {useState, useEffect} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Save} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {ConstraintType, ConstraintSeverity,
    type CreateConstraintRequest, type UpdateConstraintRequest} from '@/types/constraint'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {ExpressionEditor} from '@/components/ui/expression-editor'
import {SCHEMA_TYPES_ANY} from '@/types/expression'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Card, CardContent} from '@/components/ui/card'
import {PageLayout} from '@/components/shared/PageLayout'
import {FormField} from '@/components/shared/FormField'
import {FormGrid} from '@/components/shared/FormGrid'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {CONCEPT_ICON} from '@/lib/concepts'

export function SchemaPolicyEdit() {
    const i18n = useI18n()
    const services = useServices()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const {id} = useParams<{id: string}>()
    const isEdit = Boolean(id)

    usePageTitle(isEdit ? i18n.schemaPolicies.detailPageTitle : i18n.schemaPolicies.createPageTitle)

    const {data: existing} = useQuery({
        queryKey: ['schema-policy', id],
        queryFn: () => services.constraints.detail(id!),
        enabled: isEdit,
    })

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [type, setType] = useState<ConstraintType>(ConstraintType.CEL)
    const [expression, setExpression] = useState('')
    const [constraintCode, setConstraintCode] = useState('')
    const [severity, setSeverity] = useState<ConstraintSeverity>(ConstraintSeverity.WARN)
    const [messageTemplate, setMessageTemplate] = useState('')

    useEffect(() => {
        if (existing) {
            setName(existing.name)
            setDescription(existing.description ?? '')
            setType(existing.type)
            setExpression(existing.expression ?? '')
            setConstraintCode(existing.constraintCode ?? '')
            setSeverity(existing.severity)
            setMessageTemplate(existing.messageTemplate ?? '')
        }
    }, [existing])

    const saveMutation = useMutation({
        mutationFn: () => {
            const base: CreateConstraintRequest = {
                name,
                description: description || null,
                type,
                expression: type === ConstraintType.CEL ? expression : null,
                constraintCode: type === ConstraintType.NATIVE_CODE ? constraintCode : null,
                severity,
                messageTemplate: messageTemplate || null,
            }
            if (isEdit && existing) {
                const update: UpdateConstraintRequest = {...base, version: existing.version}
                return services.constraints.update(id!, update)
            }
            return services.constraints.create(base)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['schema-policies']})
            navigate(routes.schema.policies.list())
        },
    })

    return (
        <PageLayout
            variant="detail"
            icon={CONCEPT_ICON.constraint}
            title={isEdit ? i18n.schemaPolicies.editPolicy : i18n.schemaPolicies.createPolicy}
            data-testid="schema-policy-edit"
        >
            <Card>
                <CardContent className="space-y-6 pt-6">
                    <FormGrid columns={2}>
                        <FormField label={i18n.schemaPolicies.name} htmlFor="policy-name" required fullWidth>
                            <Input
                                id="policy-name"
                                data-testid="input-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </FormField>
                        <FormField label={i18n.schemaPolicies.description} htmlFor="policy-description" fullWidth>
                            <Textarea
                                id="policy-description"
                                data-testid="input-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={2}
                            />
                        </FormField>
                        <FormField label={i18n.schemaPolicies.type} htmlFor="policy-type" required>
                            <Select value={type} onValueChange={(v: string) => setType(v as ConstraintType)}>
                                <SelectTrigger id="policy-type" data-testid="select-type">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ConstraintType.CEL}>{i18n.schemaPolicies.typeDsl}</SelectItem>
                                    <SelectItem value={ConstraintType.NATIVE_CODE}>{i18n.schemaPolicies.typeNativeCode}</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormField>
                        <FormField label={i18n.schemaPolicies.severity} htmlFor="policy-severity" required>
                            <Select value={severity} onValueChange={(v: string) => setSeverity(v as ConstraintSeverity)}>
                                <SelectTrigger id="policy-severity" data-testid="select-severity">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ConstraintSeverity.INFO}>{i18n.schemaPolicies.severityInfo}</SelectItem>
                                    <SelectItem value={ConstraintSeverity.WARN}>{i18n.schemaPolicies.severityWarn}</SelectItem>
                                    <SelectItem value={ConstraintSeverity.ERROR}>{i18n.schemaPolicies.severityError}</SelectItem>
                                    <SelectItem value={ConstraintSeverity.CRITICAL}>{i18n.schemaPolicies.severityCritical}</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormField>
                        {type === ConstraintType.CEL && (
                            <FormField label={i18n.schemaPolicies.expression} htmlFor="policy-expression" required fullWidth>
                                <ExpressionEditor
                                    value={expression}
                                    onChange={setExpression}
                                    rows={4}
                                    schemaTypes={SCHEMA_TYPES_ANY}
                                    data-testid="input-expression"
                                />
                            </FormField>
                        )}
                        {type === ConstraintType.NATIVE_CODE && (
                            <FormField label={i18n.schemaPolicies.constraintCode} htmlFor="policy-code" required fullWidth>
                                <Input
                                    id="policy-code"
                                    data-testid="input-constraint-code"
                                    value={constraintCode}
                                    onChange={(e) => setConstraintCode(e.target.value)}
                                />
                            </FormField>
                        )}
                        <FormField label={i18n.schemaPolicies.messageTemplate} htmlFor="policy-message-template" fullWidth>
                            <Input
                                id="policy-message-template"
                                data-testid="input-message-template"
                                value={messageTemplate}
                                onChange={(e) => setMessageTemplate(e.target.value)}
                                placeholder={i18n.schemaPolicies.messageTemplatePlaceholder}
                            />
                        </FormField>
                    </FormGrid>
                    <InlineRow gap="sm" justify="end" className="pt-4">
                        <Button variant="outline" onClick={() => navigate(routes.schema.policies.list())} data-testid="btn-cancel">
                            {i18n.common.cancel}
                        </Button>
                        <Button
                            onClick={() => saveMutation.mutate()}
                            disabled={!name || saveMutation.isPending}
                            data-testid="btn-save"
                        >
                            <Icon icon={Save} size="sm" className="mr-2"/>
                            {i18n.common.save}
                        </Button>
                    </InlineRow>
                </CardContent>
            </Card>
        </PageLayout>
    )
}
